import { ENTITY_DEFS, SEXE_COLOR, STATUT_BADGE } from '~/data/entities'
import type { FieldDef } from '~/data/entities'
import type { Row } from '~/composables/useDb'
import { DISPLAY_FNS, FILTER_FNS } from '~/utils/entityFns'
import { fmtDate, fmtMoney } from '~/utils/format'

/** Un champ de la maquette, avec l'entité qui le porte (les fonctions display / filter en dépendent). */
export type Field = FieldDef & { entity: string }

/** Entités où le champ « Code » reste affiché (pédagogie : UE et matières s'identifient par leur code). */
const CODE_FIELD_ENTITIES = new Set(['unitesEnseignement', 'matieres'])

const FIELDS: Record<string, Field[]> = {}
export function fieldsOf(entityKey: string): Field[] {
  return (FIELDS[entityKey] ??= (ENTITY_DEFS[entityKey]?.fields ?? [])
    .filter(f => f.key !== 'code' || CODE_FIELD_ENTITIES.has(entityKey))
    .map(f => ({ ...f, entity: entityKey })))
}

/** Libellé d'une ligne référencée : `display` (fonction ou nom de propriété) de la maquette. */
export function labelOf(f: Field, target: Row): string {
  if (f.displayFn) return DISPLAY_FNS[`${f.entity}.${f.key}`]!(target)
  return String(target[f.display!])
}

/** Lignes proposées pour un champ « fk » / « multiselect » : filtre du champ, puis école courante. */
export function refRows(f: Field): Row[] {
  let rows = useDb().value[f.ref!] ?? []
  if (f.filterFn) rows = rows.filter(FILTER_FNS[`${f.entity}.${f.key}`]!)
  return scopedRows(f.ref!, rows)
}

/** Ce qu'une cellule affiche (équivalent des chaînes HTML de `fieldDisplayValue`). */
export type Cell =
  | { t: 'muted' }
  | { t: 'text', v: unknown }
  | { t: 'badges', items: string[] }
  | { t: 'sexe', text: string, color: string }
  | { t: 'bool', on: boolean }
  | { t: 'select', text: string, cls: string }
  | { t: 'fk', label: string, ref: string, id: string }
  | { t: 'ecole', row: Row }
  | { t: 'image', src: string }

export function fieldCell(f: Field, row: Row): Cell {
  const v = row[f.key]
  if (f.type === 'multiselect') {
    const arr = Array.isArray(v) ? v : []
    if (!arr.length) return { t: 'muted' }
    return { t: 'badges', items: arr.map((id) => {
      const t = findById(f.ref!, id)
      return t ? labelOf(f, t) : ''
    }).filter(Boolean) }
  }
  if (f.key === 'sexe') {
    if (!v) return { t: 'muted' }
    return { t: 'sexe', text: titleCase(v), color: SEXE_COLOR[String(v)] || 'inherit' }
  }
  if (f.type === 'image') return v ? { t: 'image', src: String(v) } : { t: 'muted' }
  if (f.type === 'boolean') return { t: 'bool', on: !!v }
  if (f.type === 'select') return { t: 'select', text: titleCase(v), cls: STATUT_BADGE[String(v)] || '' }
  if (f.type === 'fk') {
    const target = findById(f.ref!, v)
    if (!target) return { t: 'muted' }
    const label = labelOf(f, target)
    if (ENTITY_DEFS[f.ref!] && f.ref !== 'niveaux' && f.ref !== 'offres') return { t: 'fk', label, ref: f.ref!, id: target.id }
    return { t: 'text', v: label }
  }
  if (f.key.toLowerCase().includes('montant')) return { t: 'text', v: v !== undefined && v !== null && v !== '' ? fmtMoney(v) : '—' }
  if (f.type === 'date') return { t: 'text', v: fmtDate(v) }
  return (v === undefined || v === '' || v === null) ? { t: 'muted' } : { t: 'text', v }
}

/** `fieldRawValue` de la maquette : la valeur en texte (recherche, tri, export). */
export function fieldRawValue(f: Field, row: Row): string | number {
  const v = row[f.key]
  if (f.type === 'multiselect') {
    const arr = Array.isArray(v) ? v : []
    return arr.map((id) => {
      const t = findById(f.ref!, id)
      return t ? labelOf(f, t) : ''
    }).filter(Boolean).join(', ')
  }
  if (f.type === 'fk') {
    const target = findById(f.ref!, v)
    return target ? labelOf(f, target) : ''
  }
  if (f.type === 'boolean') return v ? 'oui' : 'non'
  if (f.type === 'image') return ''
  if (v === undefined || v === null) return ''
  return v as string | number
}

export interface Column {
  label: string
  field?: Field
  cell: (row: Row) => Cell
  text: (row: Row) => string
}

const linkCell = (label: string, ref: string, id: string): Cell => ({ t: 'fk', label, ref, id })
const etudiantOf = (insc: Row | undefined): Row | null => (insc ? findById('personnes', insc.etudiantId) ?? null : null)

/** `buildFieldColumns` de la maquette. */
export function buildFieldColumns(fields: Field[]): Column[] {
  const cols: Column[] = []
  for (const f of fields) {
    if (f.type === 'fk' && f.ref === 'inscriptions') {
      const insc = (row: Row) => findById('inscriptions', row[f.key])
      cols.push({
        label: 'Étudiant',
        cell: (row) => {
          const etu = etudiantOf(insc(row))
          return etu ? linkCell(`${etu.prenoms} ${etu.nom}`, 'etudiants', etu.id) : { t: 'muted' }
        },
        text: (row) => {
          const etu = etudiantOf(insc(row))
          return etu ? `${etu.prenoms} ${etu.nom}` : ''
        },
      })
      cols.push({
        label: 'N° Inscription',
        cell: (row) => {
          const i = insc(row)
          return i ? linkCell(String(i.numero), 'inscriptions', i.id) : { t: 'muted' }
        },
        text: (row) => {
          const i = insc(row)
          return i ? String(i.numero) : ''
        },
      })
    }
    else if (f.type === 'fk' && f.ref === 'echeanciers') {
      const resolve = (row: Row) => {
        const ech = findById('echeanciers', row[f.key])
        return ech ? findById('inscriptions', ech.inscriptionId) : undefined
      }
      const offreOf = (row: Row) => {
        const i = resolve(row)
        const classe = i && i.classeId ? findById('classes', i.classeId) : null
        return classe ? findById('offres', classe.offreId) : undefined
      }
      cols.push({
        label: 'N° Inscription',
        cell: (row) => {
          const i = resolve(row)
          return i ? linkCell(String(i.numero), 'inscriptions', i.id) : { t: 'muted' }
        },
        text: (row) => {
          const i = resolve(row)
          return i ? String(i.numero) : ''
        },
      })
      cols.push({
        label: 'Étudiant',
        cell: (row) => {
          const etu = etudiantOf(resolve(row))
          return etu ? linkCell(`${etu.prenoms} ${etu.nom}`, 'etudiants', etu.id) : { t: 'muted' }
        },
        text: (row) => {
          const etu = etudiantOf(resolve(row))
          return etu ? `${etu.prenoms} ${etu.nom}` : ''
        },
      })
      cols.push({
        label: 'Offre de formation',
        cell: (row) => {
          const o = offreOf(row)
          return o ? { t: 'text', v: String(o.libelle) } : { t: 'muted' }
        },
        text: (row) => {
          const o = offreOf(row)
          return o ? String(o.libelle) : ''
        },
      })
    }
    else {
      cols.push({
        label: f.label,
        field: f,
        cell: row => fieldCell(f, row),
        text: (row) => {
          const v = fieldRawValue(f, row)
          return v === undefined || v === null ? '' : String(v)
        },
      })
    }
  }
  return cols
}

/** `offreCyclesLabel` de la maquette. */
export function offreCyclesLabel(offreId: string): string {
  const db = useDb().value
  const niveauIds = (db.niveauOffre ?? []).filter(no => no.offreId === offreId).map(no => no.niveauId)
  const cycleIds = [...new Set(niveauIds.map((nid) => {
    const n = findById('niveaux', nid)
    return n ? n.cycleId : null
  }).filter(Boolean))]
  const libs = cycleIds.map((cid) => {
    const c = findById('cycles', cid)
    return c ? String(c.libelle) : null
  }).filter(Boolean)
  return libs.length ? libs.join(', ') : 'Aucun niveau rattaché pour l\'instant'
}
